import { type FC, useEffect } from 'react';

import { httpGetCollectionsByCollectionId } from '@iconik-io/iconik-api/src/services/assets';

import { getEntityCurrentAcls } from '../../ducks/common_acls/thunks';
import {
  selectCanUpload,
  selectIfCanChooseUploadStorage,
  selectStorageOptionDisabled,
} from '../../panels/uploads/selectors';
import { useLeafRouteParams } from '../../router/use_leaf_route_params';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import CheckboxFieldWrapper from '../../ui/forms/field_wrappers/checkbox/checkbox';
import { selectUserRoles } from '../../utils/get_user_roles';
import type { ShareSettingFieldProps } from './types';

type Props = {
  fieldProps: ShareSettingFieldProps;
  objectId: string;
  disabled?: boolean;
  labelClassName?: string;
  className?: string;
};

const UploadSwitch: FC<Props> = ({
  disabled,
  fieldProps,
  objectId,
  labelClassName,
  className,
}) => {
  const { route } = useLeafRouteParams();
  const dispatch = useAppDispatch();
  const canUpload = useAppSelector((state) =>
    selectCanUpload(state, objectId, undefined, route),
  );
  const roles = useAppSelector(selectUserRoles);
  const optionsDisabled = useAppSelector((state) =>
    selectStorageOptionDisabled(state, objectId),
  );
  const collections = useAppSelector((state) => state.entities.collections);

  const canChooseStorage = useAppSelector(selectIfCanChooseUploadStorage);

  // biome-ignore lint/correctness/useExhaustiveDependencies: mount
  useEffect(() => {
    if (!collections[objectId]) {
      dispatch(getEntityCurrentAcls('collections', objectId));
      dispatch(httpGetCollectionsByCollectionId(objectId));
    }
  }, []);

  let message: string | undefined;

  if (optionsDisabled) {
    message = 'Files will be uploaded to the collection storage';
  }
  if (!canChooseStorage) {
    message = 'Files will be uploaded to your default storage';
  }
  if (!roles.canCreateSharesToUpload || !canUpload || !collections[objectId]) {
    message = 'You do not have permission to allow uploads';
  }

  return (
    <CheckboxFieldWrapper
      {...fieldProps.allow_upload}
      name="share.allow_upload"
      key="allow_upload"
      helpMessage={message}
      disabled={disabled || !roles.canCreateSharesToUpload || !canUpload}
      label="Upload"
      labelWidth={fieldProps.allow_upload?.labelWidth}
      labelClassName={labelClassName}
      className={className}
    />
  );
};

export default UploadSwitch;
